<template>
  <div class="items-component">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="items"
      :filters.sync="tableStateStore.filters"
      :lazy="true"
      :loading="isLoading"
      :paginator="true"
      :totalRecords="getItemCount"
      :rows.sync="tableStateStore.pagination.pageSize"
      :first="tableStateStore.skip()"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 15, 20, 50, 100, 200]"
      dataKey="number"
      :rowHover="true"
      :stateKey="tableStateStore.filterName()"
      filterDisplay="row"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      stateStorage="local"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h1 class="page-title">{{ $t('item_inventory') }}</h1>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse equal-btn" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
            <router-link class="btn btn-success mb-2 equal-btn" :to="{ name: 'ItemCreatePage' }">
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('itemCreate') }}
            </router-link>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '40px' }"
        :bodyStyle="'text-align: center;width: 40px'"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.number === '_new'">
            {{ $t('_new') }}
          </span>
          <span v-else>
            <router-link
              :to="{
                name: 'ItemEditPage',
                params: { itemNumber: slotProps.data.number },
              }"
              >{{ slotProps.data.number.toString().padStart(4, '0') }}</router-link
            >
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="wholesaleNr"
        :header="$t('wholesaleNr')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '170px' }"
        :bodyStyle="{ 'text-align': 'left' }"
      >
        <template #body="slotProps">{{ slotProps.data.wholesaleNr }}</template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="industryNr"
        :header="$t('industryNr')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '190px' }"
        :bodyStyle="{ 'text-align': 'left' }"
      >
        <template #body="slotProps">{{ slotProps.data.industryNr }}</template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="description"
        :header="$t('description')"
        sortable
        :showFilterMenu="false"
        :bodyStyle="{ 'text-align': 'left' }"
      >
        <template #body="slotProps"><span v-html="slotProps.data.description"></span> </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="type"
        :header="$t('type')"
        sortable
        filterMatchMode="equals"
        :showFilterMenu="false"
        :headerStyle="{ width: '60px' }"
      >
        <template #body="slotProps">{{ $t(slotProps.data.type) }} </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.itemTypes"
            :options="itemTypes"
            @input="onTypeFilter"
            optionLabel="label"
            :placeholder="$t('type')"
          >
          </MultiSelect>
        </template>
      </Column>

      <Column
        field="solarPlant"
        :header="$t('solarPlant')"
        sortable
        filterMatchMode="equals"
        :showFilterMenu="false"
        :headerStyle="{ width: '60px' }"
      >
        <template #body="slotProps">{{ $t(slotProps.data.solarPlant) }} </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.solarPlants"
            :options="solarPlantTypes"
            @input="onSolarPlantFilter"
            optionLabel="label"
            :placeholder="$t('solarPlant')"
          >
          </MultiSelect>
        </template>
      </Column>

      <Column
        field="unit"
        :header="$t('unit')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '60px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'left' }"
      >
        <template #body="slotProps"> {{ $t(slotProps.data.unit) }} </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.units"
            :options="itemUnits"
            @input="onUnitFilter"
            optionLabel="label"
            :placeholder="$t('unit')"
          >
          </MultiSelect>
        </template>
      </Column>

      <Column
        field="supplier"
        :header="$t('supplier')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '80px' }"
        :bodyStyle="{ 'text-align': 'left' }"
      >
        <template #body="slotProps">{{ slotProps.data.supplier }}</template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="installationTimeInMinutes"
        :header="$t('installationTimeInMinutes')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '100px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
      >
        <template #body="slotProps">
          {{
            slotProps.data.installationTimeInMinutes
              ? slotProps.data.installationTimeInMinutes + ' min'
              : '-'
          }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="priceCalculation.itemCustomerPrice"
        :header="$t('itemCustomerPrice')"
        sortable
        filterField="itemCustomerPrice"
        :showFilterMenu="false"
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
      >
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.priceCalculation?.itemCustomerPrice) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="priceCalculation.laborCustomerPrice"
        filterField="laborCustomerPrice"
        :header="$t('laborCustomerPrice')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
      >
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.priceCalculation?.laborCustomerPrice) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="priceCalculation.totalCustomerPrice"
        filterField="totalCustomerPrice"
        :header="$t('totalCustomerPrice')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
      >
        <template #body="slotProps">
          <strong>{{ formatCurrency(slotProps.data.priceCalculation?.totalCustomerPrice) }}</strong>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Breadcrumbs from '@/components/Breadcrumbs';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
export default {
  name: 'ItemsPage',
  components: {
    Breadcrumbs,
    DataTable,
    Column,
    InputText,
    MultiSelect,
  },
  data() {
    return {
      loading: true,
      editingRows: [],
      backupRows: [],
      itemsTableStateConfig: {
        name: 'items',
        filters: {
          search: { value: null },
          number: { value: null },
          wholesaleNr: { value: null },
          industryNr: { value: null },
          description: { value: null },
          unit: { value: null },
          price: { value: null },
          supplier: { value: null },
          installationTimeInMinutes: { value: null },
          itemCustomerPrice: { value: null },
          laborCustomerPrice: { value: null },
          totalCustomerPrice: { value: null },
        },
        customFilters: {
          solarPlants: [],
          itemTypes: [],
          units: [],
        },
        sorting: { sortField: 'number', sortOrder: 1 },
      },
    };
  },
  computed: {
    ...mapGetters([
      'isLoading',
      'getItems',
      'getItem',
      'getItemUnits',
      'getItemsByType',
      'getItemCount',
      'getEnumValues',
      'getTableState',
    ]),
    tableStateStore() {
      return this.getTableState(this.itemsTableStateConfig.name);
    },
    solarPlantTypes() {
      if (!this.getEnumValues('ItemSolarPlant')) {
        return [];
      }
      return this.getEnumValues('ItemSolarPlant').map((solarPlant) => {
        return { value: solarPlant, label: this.$t(solarPlant) };
      });
    },
    itemUnits() {
      if (!this.getEnumValues('ItemUnit')) {
        return [];
      }
      return this.getEnumValues('ItemUnit').map((unit) => {
        return { value: unit, label: this.$t(unit) };
      });
    },
    itemTypes() {
      if (!this.getEnumValues('ItemType')) {
        return [];
      }
      return this.getEnumValues('ItemType').map((type) => {
        return { value: type, label: this.$t(type) };
      });
    },
    breadcrumbs() {
      return [{ name: 'Home', route: { name: 'home' } }, { name: this.$t('item_inventory') }];
    },
    items() {
      // return this.getItemsByType(this.itemType);
      return this.getItems;
    },
  },
  methods: {
    ...mapActions([
      'fetchItems',
      'initItem',
      'itemLocalUpdate',
      'updateItem',
      'createItem',
      'itemCleanup',
      'fetchItemsPaginated',
      'fetchEnumValues',
      'initTableState',
    ]),
    async onTypeFilter(value) {
      this.tableStateStore.customFilters.itemTypes = this.itemTypes.filter((type) =>
        value.includes(type)
      );
      await this.loadTableData();
    },
    async onSolarPlantFilter(value) {
      this.tableStateStore.customFilters.solarPlants = this.solarPlantTypes.filter((solarPlant) =>
        value.includes(solarPlant)
      );
      await this.loadTableData();
    },
    async onUnitFilter(value) {
      this.tableStateStore.customFilters.units = this.itemUnits.filter((unit) =>
        value.includes(unit)
      );
      await this.loadTableData();
    },
    filterDate(value, filter) {
      if (
        filter === undefined ||
        filter === null ||
        (typeof filter === 'string' && filter.trim() === '')
      ) {
        return true;
      }

      if (value === undefined || value === null) {
        return false;
      }

      return this.$date(value).indexOf(filter) >= 0 ? true : false;
    },
    onAddItem() {
      this.initItem();
      // this.$emit('row-edit-init', {data: {}, field: null, index: 3});
      // console.log(this.$refs);
    },
    onRowEditInit(event) {
      if (event.data._id !== '_new') {
        this.backupRows[event.index] = { ...this.getItem(event.data._id) };
      }
    },
    onRowEditCancel(event) {
      if (event.data._id === '_new') {
        this.itemCleanup();
      } else {
        this.itemLocalUpdate(this.backupRows[event.index]);
      }
    },
    async onRowEditSave(event) {
      try {
        if (event.data && event.data._id != '_new') {
          await this.updateItem(event.data);
        } else {
          const item = await this.createItem(event.data);
        }
      } catch (e) {
        // console.log(e);
      }
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },
    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },
    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },
    /**
     * Remove all filters
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     * Load remote table data
     */
    async loadTableData() {
      await this.fetchItemsPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: {
          ...this.tableStateStore.filters,
          ...this.tableStateStore.customFilters,
        },
      });
    },

    /**
     * Format currency values
     */
    formatCurrency(value) {
      if (value === null || value === undefined) {
        return '-';
      }
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
  },
  async created() {
    await this.initTableState(this.itemsTableStateConfig);
    this.loading = false;
  },

  async mounted() {
    await this.fetchEnumValues('ItemSolarPlant');
    await this.fetchEnumValues('ItemType');
    await this.fetchEnumValues('ItemUnit');
    await this.loadTableData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-clear-button {
  display: none;
  width: 0;
}
::v-deep .p-column-filter-menu-button {
  display: none;
  width: 0;
}
/** Fix Dropdown overlay */
::v-deep .p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 75vh;
}
</style>
